import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Upload,
  Button,
  notification,
  Row,
  Col,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Joi from "joi";
import axios from "axios";
import config from "../../../../config/config";

const Edit = ({ isVisible, handleClose, moduleName, base_url, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState({});
  const [fileList, setFileList] = useState([]);

  const azure_base = config.azure_object_url || "";

  const joiOptions = { abortEarly: false };

  // ---------------- VALIDATION ----------------

  const validate = (value) => {
    const schemaObj = {
      name: Joi.string().required().messages({
        "string.empty": "Name is required",
        "any.required": "Name is required",
      }),
    };

    const schema = Joi.object(schemaObj).options({
      allowUnknown: true,
    });

    const { error } = schema.validate(value, joiOptions);

    const errors = {};

    if (error) {
      error.details.forEach((field) => {
        errors[field.path[0]] = field.message;
      });
    }

    return errors;
  };

  // ---------------- PREFILL ----------------

  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        name: record.name,
      });
    }
  }, [record, form]);

  // ---------------- FILE CHANGE ----------------

  const handleFileChange = ({ fileList }) => {
    setFileList(fileList.slice(-1));
  };

  // ---------------- FILE VALIDATION ----------------

  const beforeUpload = (file) => {
    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      notification.error({
        message: "Only PDF files are allowed",
      });
      return Upload.LIST_IGNORE;
    }

    const isUnder100MB = file.size / 1024 / 1024 <= 100;

    if (!isUnder100MB) {
      notification.error({
        message: "File must be smaller than 100MB",
      });
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  // ---------------- SUBMIT ----------------

  const handleSubmit = async (value) => {
    const errors = validate(value);
    setFormError(errors);

    if (Object.keys(errors).length) return;

    const formData = new FormData();

    formData.append("name", value.name);

    if (fileList.length) {
      formData.append("catelog", fileList[0].originFileObj);
    }

    setLoading(true);

    try {
      const response = await axios.put(`${base_url}/${record._id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response?.data?.message) {
        notification.success({
          message: response.data.message,
        });

        handleClose(true);
      } else {
        notification.error({
          message: "Failed to update record",
        });
      }
    } catch (err) {
      console.error(err);

      notification.error({
        message: err?.response?.data?.message || "Something went wrong",
      });
    }

    setLoading(false);
  };

  return (
    <Modal
      title={`Edit ${moduleName}`}
      visible={isVisible}
      onCancel={() => handleClose(false)}
      okText="Update"
      cancelText="Cancel"
      confirmLoading={loading}
      onOk={() => {
        const values = form.getFieldsValue();
        handleSubmit(values);
      }}
      width={600}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              label="Catalogue Name"
              name="name"
              required
              {...(formError.name && {
                help: formError.name,
                validateStatus: "error",
              })}
            >
              <Input
                placeholder="Enter Catalogue name"
                onChange={() =>
                  setFormError((prev) => ({ ...prev, name: null }))
                }
              />
            </Form.Item>
          </Col>

          {/* CURRENT PDF */}

          {record?.catelog && (
            <Col xs={24}>
              <Form.Item label="Current PDF">
                <a
                  href={
                    azure_base + "/public/uploads/catalog/" + record.catelog
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  View Current PDF
                </a>
              </Form.Item>
            </Col>
          )}

          {/* NEW PDF */}

          <Col xs={24}>
            <Form.Item
              label="Replace PDF"
              extra="Only PDF allowed • Max size 100MB"
            >
              <Upload
                beforeUpload={beforeUpload}
                fileList={fileList}
                onChange={handleFileChange}
                accept="application/pdf"
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Select PDF</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default Edit;
